// Types pour les différentes soumissions de formulaires

export interface BaseSubmission {
  id: string
  createdAt: string
  status: 'nouveau' | 'en_cours' | 'traite' | 'archive'
}

export interface AtelierSubmission extends BaseSubmission {
  type: 'atelier'
  nom: string
  prenom: string
  dateNaissance: string
  sexe: string
  situation: string
  niveauEtudes: string
  ville: string
  quartier: string
  telephone: string
  email: string
  formationSouhaitee: string
  formationAutre?: string
  source: string
  motivations: string
}

export interface FestivalSubmission extends BaseSubmission {
  type: 'festival'
  nom: string
  prenom: string
  telephone: string
  email: string
  profil: string
  participation: string
  message: string
}

export interface EmployeurSubmission extends BaseSubmission {
  type: 'employeur'
  nom: string
  prenom: string
  typeEmployeur: string
  ville: string
  telephone: string
  email: string
  profilsRecherches: string
  attentes: string
}

export interface RecruterSubmission extends BaseSubmission {
  type: 'recruter'
  nom: string
  prenom: string
  telephone: string
  email: string
  ville: string
  quartier: string
  typePersonnel: string
  typeContrat: string
  dateDebut: string
  description: string
}

export interface PartenariatSubmission extends BaseSubmission {
  type: 'partenariat'
  organisation: string
  typeOrganisation: string
  nomContact: string
  fonction: string
  telephone: string
  email: string
  typePartenariat: string
  proposition: string
}

export interface RecyclageSubmission extends BaseSubmission {
  type: 'recyclage'
  // Employeur
  nomEmployeur: string
  telephoneEmployeur: string
  emailEmployeur: string
  villeEmployeur: string
  // Employé
  nomEmploye: string
  posteActuel: string
  anciennete: string
  telephoneEmploye: string
  // Formation
  pointsFormation: string[]
  autresPoints: string
  periodeSouhaitee: string
  dureeSouhaitee: string
  observations: string
}

export interface MembreSubmission extends BaseSubmission {
  type: 'membre'
  nom: string
  prenom: string
  telephone: string
  email: string
  ville: string
  profession: string
  typeMembre: string
  contribution: string
  motivations: string
}

export interface FormateurSubmission extends BaseSubmission {
  type: 'formateur'
  nom: string
  prenom: string
  email: string
  telephone: string
  domaine: string
  experience: string
  disponibilite: string
}

export interface ProMaisonEmployeurSubmission extends BaseSubmission {
  type: 'promaison_employeur'
  nomComplet: string
  telephone: string
  email: string
  quartier: string
  interet: string
  disponibilite: string
  commentaire: string
  consentement: string
}

export interface ProMaisonProfessionnelSubmission extends BaseSubmission {
  type: 'promaison_professionnel'
  nomComplet: string
  telephone: string
  age: string
  quartier: string
  metiers: string
  experience: string
  disponibilite: string
  consentement: string
}

export interface BenevoleSubmission extends BaseSubmission {
  type: 'benevole'
  nom: string
  prenom: string
  email: string
  telephone: string
  quartier: string
  disponibilite: string
  motivation: string
}

export interface FormationSubmission extends BaseSubmission {
  type: 'formation'
  nom: string
  prenom: string
  age: string
  ville: string
  quartier: string
  telephone: string
  whatsapp: string
  email: string
  typeFormation: string
  motivations: string
  charteEngagement: string
}

export type Submission =
  | AtelierSubmission
  | FestivalSubmission
  | EmployeurSubmission
  | RecruterSubmission
  | PartenariatSubmission
  | RecyclageSubmission
  | MembreSubmission
  | FormateurSubmission
  | BenevoleSubmission
  | FormationSubmission
  | ProMaisonEmployeurSubmission
  | ProMaisonProfessionnelSubmission

// Clés de stockage
const STORAGE_KEYS = {
  atelier: 'adcc_ateliers',
  festival: 'adcc_festivals',
  employeur: 'adcc_employeurs',
  recruter: 'adcc_recrutements',
  partenariat: 'adcc_partenariats',
  recyclage: 'adcc_recyclages',
  membre: 'adcc_membres',
  formateur: 'adcc_formateurs',
  benevole: 'adcc_benevoles',
  formation: 'adcc_formations',
  promaison_employeur: 'adcc_promaison_employeurs',
  promaison_professionnel: 'adcc_promaison_professionnels',
}

// Générer un ID unique
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

// Sauvegarder une soumission
export function saveSubmission<T extends Submission>(
  data: Omit<T, 'id' | 'createdAt' | 'status'>,
): T {
  const type = data.type as keyof typeof STORAGE_KEYS
  const key = STORAGE_KEYS[type]

  const submission = {
    ...data,
    id: generateId(),
    createdAt: new Date().toISOString(),
    status: 'nouveau' as const,
  } as T

  const existing = getSubmissions(type)
  existing.push(submission)
  localStorage.setItem(key, JSON.stringify(existing))

  return submission
}

// Récupérer les soumissions par type
export function getSubmissions<T extends Submission>(
  type: keyof typeof STORAGE_KEYS,
): T[] {
  const key = STORAGE_KEYS[type]
  const data = localStorage.getItem(key)
  return data ? JSON.parse(data) : []
}

// Récupérer toutes les soumissions
export function getAllSubmissions(): Submission[] {
  const all: Submission[] = []
  Object.keys(STORAGE_KEYS).forEach((type) => {
    const submissions = getSubmissions(type as keyof typeof STORAGE_KEYS)
    all.push(...submissions)
  })
  return all.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )
}

// Mettre à jour le statut d'une soumission
export function updateSubmissionStatus(
  type: keyof typeof STORAGE_KEYS,
  id: string,
  status: Submission['status'],
): void {
  const key = STORAGE_KEYS[type]
  const submissions = getSubmissions(type)
  const index = submissions.findIndex((s) => s.id === id)

  if (index !== -1) {
    submissions[index].status = status
    localStorage.setItem(key, JSON.stringify(submissions))
  }
}

// Supprimer une soumission
export function deleteSubmission(
  type: keyof typeof STORAGE_KEYS,
  id: string,
): void {
  const key = STORAGE_KEYS[type]
  const submissions = getSubmissions(type)
  const filtered = submissions.filter((s) => s.id !== id)
  localStorage.setItem(key, JSON.stringify(filtered))
}

// Obtenir les statistiques
export function getStats() {
  return {
    ateliers: getSubmissions('atelier').length,
    festivals: getSubmissions('festival').length,
    employeurs: getSubmissions('employeur').length,
    recrutements: getSubmissions('recruter').length,
    partenariats: getSubmissions('partenariat').length,
    recyclages: getSubmissions('recyclage').length,
    membres: getSubmissions('membre').length,
    formateurs: getSubmissions('formateur').length,
    benevoles: getSubmissions('benevole').length,
    formations: getSubmissions('formation').length,
    total: getAllSubmissions().length,
    nouveaux: getAllSubmissions().filter((s) => s.status === 'nouveau').length,
  }
}

// Exporter en CSV
export function exportToCSV(type: keyof typeof STORAGE_KEYS): string {
  const submissions = getSubmissions(type)
  if (submissions.length === 0) return ''

  const headers = Object.keys(submissions[0])
  const rows = submissions.map((s) =>
    headers
      .map((h) => {
        const value = (s as Record<string, unknown>)[h]
        if (Array.isArray(value)) return `"${value.join(', ')}"`
        if (typeof value === 'string' && value.includes(','))
          return `"${value}"`
        return value
      })
      .join(','),
  )

  return [headers.join(','), ...rows].join('\n')
}

// Données de démonstration (pour tester le dashboard)
export function seedDemoData(): void {
  // Vérifier si des données existent déjà
  if (getAllSubmissions().length > 0) return

  // Ajouter quelques données de démo
  const demoAteliers: Omit<AtelierSubmission, 'id' | 'createdAt' | 'status'>[] =
    [
      {
        type: 'atelier',
        nom: 'ADJOVI',
        prenom: 'Marie',
        dateNaissance: '1995-03-15',
        sexe: 'F',
        situation: 'sans_emploi',
        niveauEtudes: 'bepc',
        ville: 'Cotonou',
        quartier: 'Akpakpa',
        telephone: '+229 97 00 00 01',
        email: 'marie.adjovi@email.com',
        formationSouhaitee: 'entretien',
        source: 'reseaux',
        motivations:
          'Je souhaite acquérir des compétences professionnelles pour trouver un emploi stable.',
      },
      {
        type: 'atelier',
        nom: 'DOSSOU',
        prenom: 'Jean',
        dateNaissance: '1988-07-22',
        sexe: 'H',
        situation: 'emploi',
        niveauEtudes: 'bac',
        ville: 'Porto-Novo',
        quartier: 'Ouando',
        telephone: '+229 96 00 00 02',
        email: 'jean.dossou@email.com',
        formationSouhaitee: 'maintenance',
        source: 'bouche',
        motivations:
          'Améliorer mes compétences en maintenance pour évoluer dans mon travail.',
      },
    ]

  const demoMembres: Omit<MembreSubmission, 'id' | 'createdAt' | 'status'>[] = [
    {
      type: 'membre',
      nom: 'HOUNKPATIN',
      prenom: 'Sophie',
      telephone: '+229 95 00 00 03',
      email: 'sophie.h@email.com',
      ville: 'Cotonou',
      profession: 'Enseignante',
      typeMembre: 'actif',
      contribution: 'benevolat',
      motivations:
        "Je crois en la mission de l'ADCC et souhaite contribuer activement.",
    },
  ]

  const demoRecrutements: Omit<
    RecruterSubmission,
    'id' | 'createdAt' | 'status'
  >[] = [
    {
      type: 'recruter',
      nom: 'AGBOSSOU',
      prenom: 'Patrick',
      telephone: '+229 94 00 00 04',
      email: 'p.agbossou@entreprise.com',
      ville: 'Cotonou',
      quartier: 'Haie Vive',
      typePersonnel: 'menage',
      typeContrat: 'temps_plein',
      dateDebut: '2026-04-01',
      description:
        "Recherche une personne pour l'entretien de notre maison, 5 jours par semaine.",
    },
  ]

  demoAteliers.forEach((d) => saveSubmission(d))
  demoMembres.forEach((d) => saveSubmission(d))
  demoRecrutements.forEach((d) => saveSubmission(d))
}
